import { getCurrencyExponent, } from "./currencies.js";
/**
 * Static foreign-exchange rates, expressed as "units of the currency per 1
 * USD". These are a manually-maintained snapshot — they intentionally do not
 * hit a live rate feed, so figures are approximate and can drift from the
 * market. The home-screen totals that use them are a convenience summary, not
 * a settlement figure; per-group balances are always shown in the group's own
 * currency, which never goes through conversion.
 *
 * To refresh: replace the values below from any reference source (rates as of
 * 2024). Every code in `SUPPORTED_CURRENCIES` must have an entry; the
 * compile-time `Record<SupportedCurrencyCode, number>` type guarantees that.
 */
export const USD_RATES = {
    AED: 3.67,
    AFN: 71,
    ALL: 93,
    AMD: 388,
    ANG: 1.79,
    AOA: 910,
    ARS: 980,
    AUD: 1.51,
    AWG: 1.79,
    AZN: 1.7,
    BAM: 1.8,
    BBD: 2,
    BDT: 119,
    BGN: 1.8,
    BHD: 0.376,
    BIF: 2900,
    BMD: 1,
    BND: 1.34,
    BOB: 6.9,
    BRL: 5.65,
    BSD: 1,
    BTN: 84,
    BWP: 13.5,
    BYN: 3.27,
    BZD: 2.01,
    CAD: 1.38,
    CDF: 2850,
    CHF: 0.86,
    CLP: 945,
    CNY: 7.12,
    COP: 4250,
    CRC: 515,
    CUP: 24,
    CVE: 101,
    CZK: 23.2,
    DJF: 178,
    DKK: 6.85,
    DOP: 60,
    DZD: 134,
    EGP: 49,
    ERN: 15,
    ETB: 120,
    EUR: 0.92,
    FJD: 2.25,
    FKP: 0.79,
    GBP: 0.79,
    GEL: 2.72,
    GHS: 15.8,
    GIP: 0.79,
    GMD: 70,
    GNF: 8600,
    GTQ: 7.73,
    GYD: 209,
    HKD: 7.79,
    HNL: 24.8,
    HTG: 132,
    HUF: 365,
    IDR: 15700,
    ILS: 3.75,
    INR: 84,
    IQD: 1310,
    IRR: 42000,
    ISK: 138,
    JMD: 157,
    JOD: 0.709,
    JPY: 150,
    KES: 129,
    KGS: 86,
    KHR: 4080,
    KMF: 452,
    KRW: 1370,
    KWD: 0.307,
    KYD: 0.83,
    KZT: 487,
    LAK: 22000,
    LBP: 89500,
    LKR: 293,
    LRD: 193,
    LSL: 18,
    LYD: 4.85,
    MAD: 9.85,
    MDL: 17.8,
    MGA: 4600,
    MKD: 56.5,
    MMK: 2100,
    MNT: 3400,
    MOP: 8.03,
    MUR: 46.5,
    MVR: 15.4,
    MWK: 1735,
    MXN: 19.8,
    MYR: 4.3,
    MZN: 63.9,
    NAD: 18,
    NGN: 1650,
    NIO: 36.8,
    NOK: 10.85,
    NPR: 134,
    NZD: 1.66,
    OMR: 0.385,
    PAB: 1,
    PEN: 3.76,
    PGK: 3.95,
    PHP: 58,
    PKR: 278,
    PLN: 3.95,
    PYG: 7800,
    QAR: 3.64,
    RON: 4.58,
    RSD: 108,
    RUB: 97,
    RWF: 1360,
    SAR: 3.75,
    SBD: 8.4,
    SCR: 13.5,
    SDG: 601,
    SEK: 10.6,
    SGD: 1.34,
    SHP: 0.79,
    SLE: 22.7,
    SOS: 571,
    SRD: 32,
    SSP: 2700,
    STN: 22.6,
    SVC: 8.75,
    SYP: 13000,
    SZL: 18,
    THB: 34,
    TJS: 10.6,
    TMT: 3.5,
    TND: 3.1,
    TOP: 2.38,
    TRY: 34.2,
    TTD: 6.78,
    TWD: 32,
    TZS: 2720,
    UAH: 41.3,
    UGX: 3700,
    USD: 1,
    UYU: 42.5,
    UZS: 12800,
    VES: 39,
    VND: 25300,
    VUV: 119,
    WST: 2.75,
    XAF: 604,
    XCD: 2.7,
    XOF: 604,
    XPF: 110,
    YER: 250,
    ZAR: 18,
    ZMW: 26.5,
    ZWG: 13.5,
};
/**
 * Converts a minor-unit amount from one supported currency to another using
 * the static {@link USD_RATES} table.
 *
 * The amount is taken to/from major units via each currency's ISO 4217
 * exponent (never assume 2), routed through USD, and rounded to the nearest
 * minor unit of the target currency. A same-currency conversion is exact and
 * returns the input unchanged.
 */
export function convertMinor(minor, from, to) {
    if (from === to)
        return minor;
    return roundHalfAwayFromZero(convertMinorExact(minor, from, to));
}
/**
 * Like {@link convertMinor} but returns the unrounded target-currency minor
 * amount (a float). Used by aggregations that round once at the end rather
 * than per term, so summing many converted values doesn't accumulate
 * per-term rounding error.
 */
function convertMinorExact(minor, from, to) {
    if (from === to)
        return minor;
    const fromMajor = minor / 10 ** getCurrencyExponent(from);
    const usd = fromMajor / USD_RATES[from];
    const toMajor = usd * USD_RATES[to];
    return toMajor * 10 ** getCurrencyExponent(to);
}
/**
 * Rounds to the nearest integer, breaking ties away from zero so positive and
 * negative magnitudes round symmetrically. `Math.round` breaks ties toward
 * +Infinity (`-2.5 → -2`, `2.5 → 3`), which would make a credit and an equal
 * debit convert to different magnitudes.
 */
function roundHalfAwayFromZero(value) {
    return Math.sign(value) * Math.round(Math.abs(value));
}
/**
 * Totals a set of per-group balances (each in its own currency) into a single
 * `display` currency, split into the amount owed to the user and the amount
 * they owe. Each balance is converted exactly and the two sides are rounded
 * once at the end, so many small balances don't each round to zero (or each
 * round up) and drift the total. Shared by the web and mobile home screens.
 */
export function summarizeBalances(balances, display) {
    let owedExact = 0;
    let owingExact = 0;
    for (const b of balances) {
        const converted = convertMinorExact(b.balance, b.currency, display);
        if (converted > 0)
            owedExact += converted;
        else if (converted < 0)
            owingExact += -converted;
    }
    return {
        owed: roundHalfAwayFromZero(owedExact),
        owing: roundHalfAwayFromZero(owingExact),
    };
}
